import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePatchSummary } from "../hooks/queries";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/Card";
import { Pagination } from "../components/ui/Pagination";
import { Skeleton, TableSkeleton } from "../components/ui/Skeleton";
import { EmptyState, ErrorState } from "../components/ui/States";
import { Table, TBody, TD, TH, THead, TR } from "../components/ui/Table";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { formatDateTime, formatInteger, formatPercent, formatRelative } from "../lib/format";
import { DevicePatchesTab } from "./device/DevicePatchesTab";
import { cn } from "../lib/utils";

export function PatchesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page") ?? "1") || 1;
  const perPage = Number(searchParams.get("perPage") ?? "25") || 25;
  const selectedDeviceId = searchParams.get("device") ?? undefined;

  const query = usePatchSummary(page, perPage);
  const items = query.data?.items ?? [];

  const [detailOpen, setDetailOpen] = useState(true);

  function updateParams(patch: Record<string, string | undefined>) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(patch)) {
          if (value === undefined || value === "") next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  }

  // Filo toplamları (görünen sayfa üzerinden değil, tüm liste üzerinden)
  const allItems = items;
  const compliant = allItems.filter((item) => item.pendingCount === 0).length;
  const totalPending = allItems.reduce((sum, item) => sum + item.pendingCount, 0);
  const totalCritical = allItems.reduce((sum, item) => sum + item.criticalPendingCount, 0);
  const oldest = allItems.reduce((max, item) => Math.max(max, item.oldestPendingDays), 0);
  const complianceRate = allItems.length > 0 ? (compliant / allItems.length) * 100 : 0;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Patch Uyumluluğu</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Filo genelinde yama durumu; cihaz satırına tıklayarak ayrıntıya inin.
        </p>
      </div>

      {/* Özet */}
      {query.isPending ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-24" />
          ))}
        </div>
      ) : query.isError ? null : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          {[
            { label: "Filo uyumluluğu", value: formatPercent(complianceRate), hint: `${compliant}/${allItems.length} cihaz güncel` },
            { label: "Bekleyen yama", value: formatInteger(totalPending), hint: "Toplam" },
            { label: "Kritik bekleyen", value: formatInteger(totalCritical), hint: "Öncelikli" },
            { label: "En eski bekleyen", value: `${formatInteger(oldest)} gün`, hint: "En yaşlı yama" },
            { label: "Yeniden başlatma", value: formatInteger(allItems.filter((i) => i.oldestPendingDays > 30).length), hint: "30 günden eski" },
          ].map((card) => (
            <Card key={card.label}>
              <CardContent className="pt-5">
                <p className="text-muted-foreground text-xs font-medium">{card.label}</p>
                <p className="mt-1 text-2xl font-semibold tracking-tight">{card.value}</p>
                <p className="text-muted-foreground mt-1 text-xs">{card.hint}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filo tablosu */}
      <Card>
        <CardHeader>
          <CardTitle>Cihaz bazlı uyumluluk</CardTitle>
          <CardDescription>
            Bekleyen yama sayısı, kritik bekleyenler ve en eski bekleyen yamanın yaşı
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {query.isPending ? (
            <TableSkeleton rows={8} columns={5} />
          ) : query.isError ? (
            <ErrorState
              message="Patch özeti yüklenemedi."
              onRetry={() => void query.refetch()}
            />
          ) : items.length === 0 ? (
            <EmptyState title="Veri yok" description="Patch özeti bulunamadı." />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Cihaz</TH>
                  <TH>Bekleyen</TH>
                  <TH>Kritik bekleyen</TH>
                  <TH>En eski (gün)</TH>
                  <TH>Son kontrol</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {items.map((item) => (
                  <TR
                    key={item.deviceId}
                    className={cn(
                      selectedDeviceId === item.deviceId && "bg-primary/5",
                    )}
                  >
                    <TD>
                      <Link
                        to={`/devices/${item.deviceId}?tab=patches`}
                        className="text-primary font-medium hover:underline"
                      >
                        {item.hostname}
                      </Link>
                    </TD>
                    <TD>
                      {item.pendingCount === 0 ? (
                        <Badge tone="success">Güncel</Badge>
                      ) : (
                        <Badge tone="warning">{item.pendingCount} bekleyen</Badge>
                      )}
                    </TD>
                    <TD>
                      {item.criticalPendingCount > 0 ? (
                        <Badge tone="danger">{item.criticalPendingCount} kritik</Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TD>
                    <TD>
                      {item.oldestPendingDays > 0 ? (
                        <span
                          className={cn(
                            "text-sm font-medium",
                            item.oldestPendingDays > 60
                              ? "text-danger"
                              : item.oldestPendingDays > 30
                                ? "text-warning"
                                : "text-foreground",
                          )}
                        >
                          {item.oldestPendingDays} gün
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TD>
                    <TD className="text-muted-foreground whitespace-nowrap">
                      {item.lastCheckedAt ? formatRelative(item.lastCheckedAt) : "—"}
                    </TD>
                    <TD>
                      <Button
                        variant="outline"
                        size="sm"
                        aria-expanded={selectedDeviceId === item.deviceId && detailOpen}
                        onClick={() => {
                          if (selectedDeviceId === item.deviceId) {
                            setDetailOpen((open) => !open);
                          } else {
                            setDetailOpen(true);
                            updateParams({ device: item.deviceId });
                          }
                        }}
                      >
                        {selectedDeviceId === item.deviceId && detailOpen
                          ? "Gizle"
                          : "Detay"}
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}

          {query.data ? (
            <Pagination
              page={query.data.pageMeta.page}
              perPage={query.data.pageMeta.perPage}
              total={query.data.pageMeta.total}
              onPageChange={(next) => updateParams({ page: String(next) })}
              onPerPageChange={(next) =>
                updateParams({ perPage: String(next), page: undefined })
              }
            />
          ) : null}
        </CardContent>
      </Card>

      {/* Cihaz bazlı detay */}
      {selectedDeviceId && detailOpen ? (
        <section aria-label="Seçili cihazın patch kayıtları" className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold">
              Cihaz detayı —{" "}
              <span className="text-muted-foreground font-normal">
                {items.find((item) => item.deviceId === selectedDeviceId)?.hostname ?? selectedDeviceId}
              </span>
            </h2>
            <Button variant="ghost" size="sm" onClick={() => updateParams({ device: undefined })}>
              Kapat
            </Button>
          </div>
          <DevicePatchesTab deviceId={selectedDeviceId} />
          <p className="text-muted-foreground text-xs">
            Son kontrol:{" "}
            {formatDateTime(
              items.find((item) => item.deviceId === selectedDeviceId)?.lastCheckedAt,
            )}
          </p>
        </section>
      ) : null}
    </div>
  );
}